import axios from 'axios';
import {
  AddressPerimetersResponse,
  AddressResponse,
  ApiAddressResponse,
  BuildingDetails,
  BuildingHeatingResponse,
  DecisionTreeResponse,
  DistrictHeatingResponse,
  ParcelBuildingResponse,
  ParcelDetails,
  Perimeter,
  PerimeterResponse,
  PriceCalculationResponse
} from '@/types/ApiTypes';
import { FormValues } from '@/types/ContactForm.ts';
import { Address } from '@/types/Address.ts';
import { useAddressStore } from '@/stores/useAddressStore';

const nodeRedUrl =
  import.meta.env.VITE_MODE !== 'production'
    ? 'https://nodered-staging.services.swissenergyplanning.ch'
    : 'https://nodered.services.swissenergyplanning.ch';

type EBLSharedData = {
  perimeterName: string | null;
  perimeterId: string | null;
  language: string;
  leadStatus: string;
  leadOrigin: string;
  constructionYear: number | null;
  objectCategory: string | null;
  districtNumber: number | null;
  parcelNumber: string | null;
  heatingType: number | null;
  heatingGeneratorType: number | null;
  gasConsumption: number | null;
  oilConsumption: number | null;
  // TODO: Customer attributes should be moved to FormValues
  //  currently we don't have the input fields for these attributes
  customerCompany: string | null;
  customerFunction: string | null;
};

interface CampaignData {
  category: 'form' | 'calculator' | 'additional';
  key: string;
  value: string | number | null;
}

const mapFormDataToApiFormat = (
  formData: FormValues,
  addressData: Address,
  calculatorData: PriceCalculationResponse,
  additionalData?: EBLSharedData,
  createLead: boolean = false
) => {
  const newCampaignData: CampaignData[] = [];

  newCampaignData.push(
    {
      category: 'form',
      key: 'title',
      value: formData.title
    },
    {
      category: 'form',
      key: 'firstname',
      value: formData.firstName
    },
    {
      category: 'form',
      key: 'lastname',
      value: formData.lastName
    },
    {
      category: 'form',
      key: 'email',
      value: formData.email
    },
    {
      category: 'form',
      key: 'phone',
      value: formData.phoneNumber
    },
    {
      category: 'form',
      key: 'custom_message_customer_number',
      value: formData.customerNumber
    },
    {
      category: 'form',
      key: 'address',
      value: addressData.id
    },
    {
      category: 'form',
      key: 'contact-address',
      value: formData.address?.id || null
    },
    {
      category: 'form',
      key: 'contacttype',
      value: formData.isOwner === 'yes' ? 'Owner' : 'Not Owner'
    },
    {
      category: 'form',
      key: 'custom_message_owner_title',
      value: [
        formData.ownerFirstName,
        formData.ownerLastName,
        formData.ownerCompanyName,
        formData.ownerAddress?.id
      ].some((value) => !!value)
        ? formData.ownerTitle
        : null
    },
    {
      category: 'form',
      key: 'custom_message_owner_firstname',
      value: formData.ownerFirstName || null
    },
    {
      category: 'form',
      key: 'custom_message_owner_lastname',
      value: formData.ownerLastName || null
    },
    {
      category: 'form',
      key: 'custom_message_owner_company',
      value: formData.ownerCompanyName || null
    },
    {
      category: 'form',
      key: 'custom_message_owner_address',
      value: formData.ownerAddress?.id || null
    }
  );

  Object.entries(calculatorData).forEach(([key, value]) => {
    newCampaignData.push({
      category: 'calculator',
      key,
      value: `${value}`
    });
  });

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      newCampaignData.push({
        category: 'additional',
        key: `custom_message_${key}`,
        value: `${value}`
      });
    });
  }

  return {
    campaignData: newCampaignData,
    formsubmitted: createLead ? 'true' : 'false',
    campaignName: 'marketsense_waermecheck'
  };
};

export const updateCampaignData = async (
  data: FormValues,
  address: Address,
  price: PriceCalculationResponse,
  createLead: boolean,
  language: string,
  sessionId: string // see src/stores/useSessionStore.tsx
) => {
  const addressStore = useAddressStore.getState();

  const lng = language === 'de-CH' ? 'de' : 'fr';

  const newAdditionalData: EBLSharedData = {
    perimeterName: addressStore.districtHeating?.perimeter || null,
    perimeterId: null,
    language: lng,
    leadStatus: createLead ? 'Lead' : 'Potential',
    leadOrigin: 'landing-page',
    constructionYear: addressStore.buildingDetails?.constructionYear || null,
    objectCategory: addressStore.buildingDetails?.buildingCategoryShort || null,
    districtNumber: addressStore.buildingDetails?.districtNumber || null,
    parcelNumber: addressStore.parcelDetails?.parcelNumber || null,
    heatingType: addressStore.buildingHeating?.heatingSource1 || null,
    heatingGeneratorType: addressStore.buildingHeating?.heatingGenerator1 || null,
    gasConsumption:
      addressStore.buildingHeating?.heatingSource1 === 7530 ? null : price.consumption,
    oilConsumption:
      addressStore.buildingHeating?.heatingSource1 === 7530 ? price.consumption : null,
    customerCompany: null,
    customerFunction: null
  };

  const payload = mapFormDataToApiFormat(data, address, price, newAdditionalData, createLead);

  const response = await axios.put(`/api/campaigndata/${sessionId}`, payload, {
    headers: { 'Content-Type': 'application/json' }
  });

  return response.data;
};

export const getAddress = async (query: string): Promise<AddressResponse[]> => {
  const formData = new FormData();
  formData.append('searchtext', query);
  const response = await axios.post('/api/searchaddress-advanced', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.rows.map((address: ApiAddressResponse) => ({
    id: address.fields.id,
    label: address.fields.address,
    street: address.fields.street,
    houseNumber: address.fields.housenumber,
    zip: address.fields.plz,
    town: address.fields.town,
    lat: address.fields.lat_long.split(';')[0],
    lon: address.fields.lat_long.split(';')[1],
    group: 'address',
    isCustom: false
  }));
};

export const getParcelBuilding = async (addressId: string): Promise<ParcelBuildingResponse> => {
  const response = await axios.get(
    `/api/marketsense/geometries/parcelbuilding-by-addressid/${addressId}`
  );
  return response.data;
};

export const getAddressByCoordinates = async ({ lat, lng }: { lat: number; lng: number }) => {
  const response = await axios.get(`/api/marketsense/addresspoints-by-coordinates/${lng}/${lat}`);

  return response.data;
};

export const getDecisionTree = async (addressId: string): Promise<DecisionTreeResponse> => {
  const response = await axios.get(`${nodeRedUrl}/decisiontree/landingpage/${addressId}`);
  return {
    perimeter: response.data.perimeter,
    status: response.data.status,
    heatingPowerDemand: response.data.bedarfheizleistung,
    heatingDemand: response.data.bedarfheizung,
    hotWaterDemand: response.data.bedarfwarmwasser,
    isEblDistrict: response.data.eblgemeinde,
    canton: response.data.kanton,
    onHoldArea: response.data.onhold,
    developmentArea: response.data.entwicklung,
    projectArea: response.data.projekt
  };
};

export const getDistrictHeating = async (addressId: string): Promise<DistrictHeatingResponse> => {
  const response = await axios.get(`/api/marketsense/fw-perimeter-by-addressid/${addressId}`);
  const data = response.data[0];
  return {
    activation: data.activation,
    activationUntil: data.activationUntil,
    addressId: data.addressid,
    campaign: data.campaign,
    edId: data.edid,
    egId: data.egid,
    language: data.language,
    perimeter: data.perimeter,
    provider: data.provider,
    origin: data.origin,
    status: data.status,
    id: data.id,
    tenant: data.mandant,
    user: data.user
  };
};

export const getBuildingHeating = async (addressId: string): Promise<BuildingHeatingResponse> => {
  const response = await axios.get(
    `/api/marketsense/heating-addresspoint-by-addressid/${addressId}`
  );
  const data = response.data[0];

  return {
    id: data.id,
    heatingUpdateDate1: data.aktualisierungsdatumHeizung1,
    heatingUpdateDate2: data.aktualisierungsdatumHeizung2,
    hotWaterUpdateDate1: data.aktualisierungsdatumWarmwasser1,
    hotWaterUpdateDate2: data.aktualisierungsdatumWarmwasser2,
    latitude: data.lat,
    longitude: data.long,
    spatialReferenceId: data.srid,
    externalGeoAddressId: data.egaid,
    buildingId: data.buildingIdSep,
    parcelId: data.parcelIdSep,
    energyDemandId: data.edid,
    energyGroupId: data.egid,
    energyReferenceArea: data.energiebezugsflaeche,
    municipalityNumber: data.gemeindenummer,
    heatingInfoSource1: data.informationsquelleHeizung1,
    heatingInfoSource2: data.informationsquelleHeizung2,
    hotWaterInfoSource1: data.informationsquelleWarmwasser1,
    hotWaterInfoSource2: data.informationsquelleWarmwasser2,
    heatingGenerator1: data.waermeerzeugerHeizung1,
    heatingGenerator2: data.waermeerzeugerHeizung2,
    hotWaterGenerator1: data.waermeerzeugerWarmwasser1,
    hotWaterGenerator2: data.waermeerzeugerWarmwasser2,
    heatingSource1: data.waermequelleHeizung1,
    heatingSource2: data.waermequelleHeizung2,
    hotWaterSource1: data.waermequelleWarmwasser1,
    hotWaterSource2: data.waermequelleWarmwasser2,
    heatingDemand: data.bedarfheizung,
    heatingPowerDemand: data.bedarfheizleistung,
    hotWaterDemand: data.bedarfwarmwasser,
    geothermalZoneCodes: data.erdsondenzoneCodes,
    groundwaterUsageCodes: data.grundwassernutzungCodes,
    heatingTypePrediction: data.heatingTypePrediction,
    fallbackHeatingCode: data.energyplanHeatingFallbackCode
  };
};

export const getAddressPerimeters = async (
  addressId: string
): Promise<AddressPerimetersResponse> => {
  const response = await axios.get(`/api/marketsense/fw-perimeter-by-addressid/${addressId}`);
  const data = response.data[0];
  return {
    activationDate: data.activation,
    activationEndDate: data.activationUntil,
    addressId: data.addressid,
    campaign: data.campaign,
    edid: data.edid,
    egid: data.egid,
    language: data.language,
    perimeter: data.perimeter,
    provider: data.provider,
    origin: data.origin,
    status: data.status,
    id: data.id,
    tenant: data.mandant,
    user: data.user,
    description: data.description
  };
};

export const calculatePrice = async (
  connectionPower: number,
  consumption: number,
  zipCode: string
): Promise<PriceCalculationResponse> => {
  const response = await axios.get(`${nodeRedUrl}/calculator`, {
    params: {
      anschlussleistung: connectionPower,
      verbrauch: consumption,
      plz: zipCode
    }
  });

  const data = response.data;

  return {
    connectionPower: data.anschlussleistung,
    consumption: data.verbrauch,
    connectionFee: data.anschlussbeitrag,
    connectionFeePerKW: data.anschlussbeitragkW,
    connectionFeeVat: data.anschlussbeitragMwst,
    connectionFeeFlatRate: data.anschlussbeitragFlatRate,
    connectionFeeMin: data.anschlussbeitragMin,
    basePrice: data.grundpreis,
    basePricePerKW: data.grundpreiskW,
    basePriceMin: data.grundpreisMin,
    basePriceFlatRate: data.grundpreisFlatRate,
    zipCode: data.plz,
    energyPrice: data.arbeitspreis,
    energyPricePerKWh: data.arbeitspreiskWh,
    annualHeatingPrice: data.jahreswärmepreis,
    annualHeatingPriceVat: data.jahreswärmepreisMwst,
    connectionFeeTotal: data.anschlussbeitragTotal,
    annualHeatingPriceTotal: data.jahreswärmepreisTotal,
    totalAmount: data.gesamtbetrag,
    vatRate: data.mehrwertsteuer,
    combinedHeatSupplyId: data.ebl_combinedheatsupplyid,
    combinedHeatSupply: data.ebl_combinedheatsupply
  };
};

export const getPdf = async (url: string) => {
  const response = await axios.get(url, { responseType: 'blob' });
  return response.data;
};

export const getPerimeterList = async (): Promise<Perimeter[]> => {
  const response = await axios.get('/api/marketsense/fw-perimeter-all-centroid');

  return response.data
    .filter((perimeter: PerimeterResponse) => perimeter.lat && perimeter.long)
    .map((perimeter: PerimeterResponse) => ({
      id: `${perimeter.lat}-${perimeter.long}`,
      label: perimeter.perimeter,
      long: perimeter.long,
      lat: perimeter.lat,
      group: 'perimeter',
      isCustom: false,
      description: perimeter.description
    }));
};

export const getBuildingDetails = async (addressId: string): Promise<BuildingDetails> => {
  const response = await axios.get(`/api/marketsense/addresspoint-by-addressid/${addressId}`);

  const data = response.data[0];

  return {
    numberOfFloors: data.anzahlGeschosse,
    numberOfRooms: data.anzahlZimmer,
    numberOfApartments: data.anzahlWohnungen,
    approvalDate: data.approvedSince,
    buildingPermit: data.baugesuch,
    constructionYearEnd: data.baujahrEnde,
    constructionYear: data.baujahrInt,
    constructionYearStart: data.baujahrStart,
    maxNeighborBuildingIndex: data.baumassenzifferMaxNeighbour,
    parcelBuildingIndex: data.baumassenzifferParzelle,
    feedInTariff: data.einspeisetarif,
    referenceTariff: data.bezugstarif,
    buildingArea: data.buildingArea,
    buildingAreaPercentage: data.buildingAreaParcel,
    buildingId: data.buildingIdSep,
    canton: data.canton,
    constructionStartDate: data.constructionSince,
    district: data.district,
    districtNumber: data.districtNumber,
    districtType: data.districtType,
    existingSince: data.existingSince,
    buildingFloorArea: data.gebFlaeche,
    buildingCategory: data.gebKategorie,
    buildingCategoryShort: data.gebKategorieShort,
    buildingClass: data.gebKlasse,
    buildingStatus: data.gebStatus,
    language: data.language,
    latitude: data.lat,
    longitude: data.long,
    spatialReferenceId: data.srid,
    parcelArea: data.parcelArea,
    parcelId: data.parcelIdSep,
    plannedSince: data.plannedSince,
    population: data.population,
    populationGrowthRate: data.populationGrowth,
    populationDensityPerHectare: data.populationHectar,
    publicTransportAccessibility: data.publicTransportQuality,
    renovationPressure: data.renovationPressure,
    renovationYearEnd: data.renovationsjahrEnde,
    renovationYear: data.renovationsjahrInt,
    renovationYearStart: data.renovationsjahrStart,
    status: data.status,
    apartmentVacancyRate: data.wohnungsleerstandProzent,
    zone: data.zone,
    energyDemandId: data.edid,
    energyGroupId: data.egid,
    houseNumber: data.houseNumber,
    street: data.street,
    zipCode: data.swissZipCode,
    town: data.town,
    id: data.id,
    rowVersion: data.rowVersionString
  };
};

export const getParcelDetails = async (addressId: string): Promise<ParcelDetails> => {
  const response = await axios.get(`/api/marketsense/parcelfeatures-by-addressid/${addressId}`);
  const data = response.data[0];

  return {
    maxNeighborBuildingIndex: data.baumassenzifferMaxNeighbour,
    parcelBuildingIndex: data.baumassenzifferParzelle,
    cantonConstructionZone: data.bauzoneKanton,
    municipalConstructionZone: data.bauzoneGemeinde,
    districtNumber: data.districtNumber,
    egrid: data.egrid,
    oerebLink: data.oerebLink,
    canton: data.canton,
    geothermalZoneCodes: data.erdsondenzoneCodes,
    groundwaterUsageCodes: data.grundwassernutzungCodes,
    isEmptyParcel: data.isEmptyParcel,
    latitude: data.lat,
    longitude: data.long,
    spatialReferenceId: data.srid,
    parcelArea: data.parcelArea,
    parcelId: data.parcelIdSep,
    parcelPerimeter: data.parcelPerimeter,
    parcelNumber: data.parzNr,
    populationDensityPerHectare: data.populationHectar,
    publicTransportAccessibility: data.publicTransportQuality,
    undergroundParcelArea: data.undergroundAreaParcel,
    totalParkingAreaParcel: data.parkingAreaParcelTotal,
    surfaceParkingAreaParcel: data.parkingAreaParcelSurface,
    zone: data.zone,
    photovoltaicProductionPower: data.pvProdPowerParcel,
    groundwaterUsageMinPowers: data.grundwassernutzungMinpowers,
    groundwaterUsageMinPowersMinergie: data.grundwassernutzungMinpowersMinergie,
    geothermalMaxDrillDepthMinParcel: data.erdsondenMaxdrilldepthMinParcel,
    geothermalMaxDrillDepthMaxParcel: data.erdsondenMaxdrilldepthMaxParcel,
    groundwaterThicknessMinParcel: data.groundwaterThicknessMinParcel,
    groundwaterThicknessMaxParcel: data.groundwaterThicknessMaxParcel
  };
};
